import { useState, useEffect, useCallback } from 'react';

const WIKI_BASE = '/wiki';
const CONFIG_URL = `${WIKI_BASE}/config.json`;
const ADMIN_STATE_URL = `${WIKI_BASE}/admin-state.json`;

const STATIC_MANIFEST_URL = `${WIKI_BASE}/manifest.json`;
const STATIC_PAGES_DIR = `${WIKI_BASE}/pages`;

const cache = {};

/* ── API helper ──────────────────────────────────────────── */

function getApiBase(config) {
  if (!config?.wikiApiUrl) return null;
  if (process.env.NODE_ENV === 'development') return '/wikiapi';
  return config.wikiApiUrl;
}

function wikiApiFetch(config, path, options = {}) {
  const base = getApiBase(config);
  const key = config?.wikiApiKey;
  if (!base) return Promise.reject(new Error('no_api'));
  const headers = { ...(options.headers || {}) };
  if (key) headers['X-API-Key'] = key;
  return fetch(`${base}${path}`, { ...options, headers });
}

export function regeneratePage(config, slug) {
  const filename = slug.endsWith('.md') ? slug : `${slug}.md`;
  return wikiApiFetch(
    config,
    `/pages/${encodeURIComponent(filename)}/regenerate`,
    { method: 'POST' },
  ).then((r) => {
    if (!r.ok) {
      if (r.status === 409) throw new Error('already_in_progress');
      if (r.status === 404) throw new Error('not_found');
      throw new Error('regeneration_failed');
    }
    return r.json();
  }).then((data) => {
    const ck = `_page_${slug}`;
    const pageObj = {
      slug,
      title: slugToTitle(slug),
      content: data.content || '',
      path: data.path || filename,
    };
    cache[ck] = pageObj;
    return pageObj;
  });
}

function adminApiCall(action, slug, extra = {}) {
  const password =
    cache._config?.adminPassword || 'bheri2026';
  return fetch('/api/admin-state', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Password': password,
    },
    body: JSON.stringify({ action, slug, ...extra }),
  }).then((r) => r.json());
}

export function useAdminState() {
  const [adminState, setAdminState] = useState(cache._adminState || null);

  useEffect(() => {
    if (cache._adminState) return;
    fetch(ADMIN_STATE_URL)
      .then((r) => r.json())
      .then((data) => {
        cache._adminState = data;
        setAdminState(data);
      })
      .catch(() => {
        const empty = { approved: {}, comments: {} };
        cache._adminState = empty;
        setAdminState(empty);
      });
  }, []);

  const refresh = useCallback((newState) => {
    cache._adminState = newState;
    setAdminState(newState);
  }, []);

  return { adminState, refresh };
}

/* ── Utility helpers ─────────────────────────────────────── */

export function slugToTitle(slug) {
  if (!slug) return '';
  return slug
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function estimateReadingTime(text) {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 220));
}

function textToId(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

export function processContent(rawContent) {
  if (!rawContent) return { body: '', references: [], headings: [], readingTime: 1 };

  const refSectionRe =
    /^#{2,3}\s*(Sources?\s*(?:and|&)?\s*References?|References?\s*(?:and|&)?\s*Sources?|References?|Sources?)\s*$/im;
  const refMatch = rawContent.match(refSectionRe);

  let body = rawContent;
  let refsRaw = '';

  if (refMatch) {
    const idx = rawContent.indexOf(refMatch[0]);
    body = rawContent.substring(0, idx).trim();
    refsRaw = rawContent.substring(idx + refMatch[0].length).trim();
  }

  const references = [];
  const seenIds = new Set();
  let autoId = 1;

  function addRef(id, text) {
    if (seenIds.has(id)) return;
    references.push({ id, text: text.trim() });
    seenIds.add(id);
  }

  refsRaw
    .split('\n')
    .filter((l) => l.trim())
    .forEach((line) => {
      const stripped = line.trim().replace(/^[-•*]\s*/, '');
      let m;

      // Old format: ref1: text
      m = stripped.match(/^ref(\d+):\s*(.+)$/);
      if (m) { addRef(parseInt(m[1], 10), m[2]); return; }

      // [N] text  or  **[N]** text  or  **[N]**: text
      m = stripped.match(/^\*{0,2}\[(\d+)\]\*{0,2}[:\s]\s*(.+)$/);
      if (m) { addRef(parseInt(m[1], 10), m[2]); return; }

      // [N][M][O]... text  (multiple ids sharing one source line)
      m = stripped.match(/^((?:\*{0,2}\[\d+\]\*{0,2})+)[:\s]\s*(.+)$/);
      if (m) {
        [...m[1].matchAll(/\[(\d+)\]/g)].forEach((id) =>
          addRef(parseInt(id[1], 10), m[2]),
        );
        return;
      }

      // N. text
      m = stripped.match(/^(\d+)\.\s+(.+)$/);
      if (m) { addRef(parseInt(m[1], 10), m[2]); return; }

      // text [N][M]... at end of line
      m = stripped.match(/^(.+?)\s+((?:\[\d+\])+)\s*$/);
      if (m) {
        [...m[2].matchAll(/\[(\d+)\]/g)].forEach((id) =>
          addRef(parseInt(id[1], 10), m[1]),
        );
        return;
      }

      // Skip noise lines (artifacts and "no sources" boilerplate)
      if (
        /^=+\s*END OF SOURCE/i.test(stripped) ||
        /^no\s+(sources?|information)\s+(provided|cited|available|were)/i.test(stripped) ||
        /^\(no(ne|.*source).*\)$/i.test(stripped) ||
        /^note:\s*the provided source data was empty/i.test(stripped)
      )
        return;

      // Fallback: plain text line — auto-number
      if (stripped) {
        while (seenIds.has(autoId)) autoId++;
        addRef(autoId, stripped);
        autoId++;
      }
    });

  references.sort((a, b) => a.id - b.id);

  const refMap = {};
  references.forEach((r) => {
    refMap[r.id] = r.text;
  });

  const hasH2 = /^## [^#]/m.test(body);
  const hasH3 = /^### /m.test(body);
  if (!hasH2 && hasH3) {
    body = body.replace(/^####\s/gm, '### ');
    body = body.replace(/^###\s/gm, '## ');
  }

  const headings = [];
  const headingRe = /^(#{2,3})\s+(.+)$/gm;
  let hm;
  while ((hm = headingRe.exec(body)) !== null) {
    const text = hm[2]
      .replace(/\\?\[ref\d+\]\\?/g, '')
      .replace(/\[\d+\]/g, '')
      .replace(/[*_`]/g, '')
      .trim();
    if (text) headings.push({ level: hm[1].length, text, id: textToId(text) });
  }

  // Old-format citations: [ref1]
  body = body.replace(/\\?\[ref(\d+)\]\\?/g, (_match, num) => {
    const n = parseInt(num, 10);
    const tip = (refMap[n] || '').replace(/"/g, '&quot;');
    return `<sup class="wiki-cite" data-ref="${n}" title="${tip}"><a href="#wiki-ref-${n}">${n}</a></sup>`;
  });

  // New-format citations: [1] (not followed by '(' to avoid matching markdown links)
  body = body.replace(/\[(\d+)\](?!\()/g, (_match, num) => {
    const n = parseInt(num, 10);
    const tip = (refMap[n] || '').replace(/"/g, '&quot;');
    return `<sup class="wiki-cite" data-ref="${n}" title="${tip}"><a href="#wiki-ref-${n}">${n}</a></sup>`;
  });

  // Parenthetical citations: (Source 1), (Sources 1, 2), (Source 1-3), (refs 1, 2)
  body = body.replace(
    /\((?:Sources?|Refs?)[:\s]+(\d[\d,\s-]*)\)/gi,
    (_match, nums) => {
      const ids = [];
      nums.replace(/(\d+)\s*-\s*(\d+)/g, (_m, a, b) => {
        for (let i = +a; i <= +b; i++) ids.push(i);
      });
      nums.replace(/(?:^|,)\s*(\d+)(?!\s*-)/g, (_m, n) => {
        const v = +n;
        if (!ids.includes(v)) ids.push(v);
      });
      return ids
        .map((n) => {
          const tip = (refMap[n] || '').replace(/"/g, '&quot;');
          return `<sup class="wiki-cite" data-ref="${n}" title="${tip}"><a href="#wiki-ref-${n}">${n}</a></sup>`;
        })
        .join('');
    },
  );

  // Repeated-word format: (SOURCE 2, SOURCE 3) or (REF 1, REF 4)
  body = body.replace(
    /\((?:Sources?|Refs?)[\s:]+\d+(?:[,\s]+(?:Sources?|Refs?)[\s:]+\d+)*\)/gi,
    (match) => {
      const ids = [...match.matchAll(/\d+/g)].map((m) => +m[0]);
      return ids
        .map((n) => {
          const tip = (refMap[n] || '').replace(/"/g, '&quot;');
          return `<sup class="wiki-cite" data-ref="${n}" title="${tip}"><a href="#wiki-ref-${n}">${n}</a></sup>`;
        })
        .join('');
    },
  );

  return { body, references, headings, readingTime: estimateReadingTime(rawContent) };
}

/* ── Hooks ───────────────────────────────────────────────── */

export function useWikiConfig() {
  const [config, setConfig] = useState(cache._config || null);
  const [loading, setLoading] = useState(!cache._config);

  useEffect(() => {
    if (cache._config) return;
    fetch(CONFIG_URL)
      .then((r) => r.json())
      .then((d) => {
        cache._config = d;
        setConfig(d);
      })
      .catch(() => setConfig({}))
      .finally(() => setLoading(false));
  }, []);

  return { config: config || {}, loading };
}

/* ── Old static manifest loader (commented out — kept for fallback) ──
export function useWikiManifest_static() {
  const { config } = useWikiConfig();
  const [manifest, setManifest] = useState(cache._manifest || []);
  const [loading, setLoading] = useState(!cache._manifest);
  useEffect(() => {
    if (cache._manifest) return;
    const url = config.apiBaseUrl
      ? `${config.apiBaseUrl}/manifest`
      : STATIC_MANIFEST_URL;
    fetch(url)
      .then((r) => r.json())
      .then((d) => { cache._manifest = d; setManifest(d); })
      .catch(() => setManifest([]))
      .finally(() => setLoading(false));
  }, [config]);
  return { manifest, loading };
}
── */

function pathToSlug(filename) {
  return filename.replace(/\.md$/i, '');
}

const BATCH_SIZE = 10000;

function parseBatch(pages) {
  const alpha = [];
  const rest = [];
  (pages || []).forEach((p) => {
    const item = { slug: pathToSlug(p.path), path: p.path };
    if (/^[a-zA-Z]/.test(p.path)) alpha.push(item);
    else rest.push(item);
  });
  return { alpha, rest };
}

export function useWikiManifest() {
  const { config, loading: configLoading } = useWikiConfig();
  const [manifest, setManifest] = useState(cache._manifest || []);
  const [loading, setLoading] = useState(!cache._manifest);
  const [totalCount, setTotalCount] = useState(cache._manifestTotal || 0);

  useEffect(() => {
    if (cache._manifestDone || configLoading) return;
    if (!config?.wikiApiUrl) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let alphaItems = [];
    let restItems = [];

    async function loadPages() {
      try {
        let page = 1;
        let total = 0;

        while (!cancelled) {
          const r = await wikiApiFetch(
            config,
            `/pages/list?page=${page}&limit=${BATCH_SIZE}`,
          );
          const d = await r.json();
          if (cancelled) return;

          if (page === 1) {
            total = d.total || 0;
            setTotalCount(total);
          }

          const batch = parseBatch(d.pages);
          alphaItems = alphaItems.concat(batch.alpha);
          restItems = restItems.concat(batch.rest);
          setManifest(alphaItems.concat(restItems));

          if (page === 1) setLoading(false);

          if ((d.pages || []).length < BATCH_SIZE) break;
          page++;
        }

        if (!cancelled) {
          cache._manifest = alphaItems.concat(restItems);
          cache._manifestTotal = alphaItems.length + restItems.length;
          cache._manifestDone = true;
        }
      } catch (e) {
        if (!cancelled) {
          try {
            const r = await fetch(STATIC_MANIFEST_URL);
            const d = await r.json();
            if (!cancelled) {
              cache._manifest = d;
              cache._manifestDone = true;
              setManifest(d);
              setTotalCount(d.length);
            }
          } catch (_) {
            if (!cancelled) setManifest([]);
          }
          if (!cancelled) setLoading(false);
        }
      }
    }

    loadPages();
    return () => { cancelled = true; };
  }, [config, configLoading]);

  return { manifest, loading, totalCount };
}

/* ── Old static page loader (commented out — kept for fallback) ──
export function useWikiPage_static(slug) {
  const { config } = useWikiConfig();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!slug) return;
    setLoading(true); setError(null);
    const localKey = `wiki_edit_${slug}`;
    const stored = localStorage.getItem(localKey);
    if (stored) { try { setPage(JSON.parse(stored)); setLoading(false); return; } catch (_) {} }
    const ck = `_page_${slug}`;
    if (cache[ck]) { setPage(cache[ck]); setLoading(false); return; }
    const url = config.apiBaseUrl
      ? `${config.apiBaseUrl}/pages/${slug}`
      : `${STATIC_PAGES_DIR}/${slug}.json`;
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error('not_found'); return r.json(); })
      .then((d) => { cache[ck] = d; setPage(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, config]);
  const savePage = useCallback((newContent) => {
    const updated = { ...(page || {}), content: newContent };
    localStorage.setItem(`wiki_edit_${slug}`, JSON.stringify(updated));
    cache[`_page_${slug}`] = updated;
    setPage(updated);
  }, [slug, page]);
  return { page, loading, error, savePage };
}
── */

export function useWikiPage(slug) {
  const { config } = useWikiConfig();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    const localKey = `wiki_edit_${slug}`;
    const stored = localStorage.getItem(localKey);
    if (stored) {
      try {
        setPage(JSON.parse(stored));
        setLoading(false);
        return;
      } catch (_) {
        /* fall through */
      }
    }

    const ck = `_page_${slug}`;
    if (cache[ck]) {
      setPage(cache[ck]);
      setLoading(false);
      return;
    }

    const filename = slug.endsWith('.md') ? slug : `${slug}.md`;
    wikiApiFetch(config, `/pages/${encodeURIComponent(filename)}`)
      .then((r) => {
        if (!r.ok) throw new Error('not_found');
        return r.json();
      })
      .then((data) => {
        const pageObj = {
          slug,
          title: slugToTitle(slug),
          content: data.content || '',
          path: data.path || filename,
        };
        cache[ck] = pageObj;
        setPage(pageObj);
      })
      .catch(() =>
        fetch(`${STATIC_PAGES_DIR}/${slug}.json`)
          .then((r) => {
            if (!r.ok) throw new Error('not_found');
            return r.json();
          })
          .then((d) => {
            cache[ck] = d;
            setPage(d);
          })
          .catch((e) => setError(e.message)),
      )
      .finally(() => setLoading(false));
  }, [slug, config]);

  const savePage = useCallback(
    (newContent) => {
      const updated = { ...(page || {}), content: newContent };
      localStorage.setItem(`wiki_edit_${slug}`, JSON.stringify(updated));
      cache[`_page_${slug}`] = updated;
      setPage(updated);
    },
    [slug, page],
  );

  return { page, loading, error, savePage };
}

export function useWikiAdmin() {
  const [isAdmin, setIsAdmin] = useState(
    () => sessionStorage.getItem('wiki_admin') === 'true',
  );

  const login = useCallback(() => {
    sessionStorage.setItem('wiki_admin', 'true');
    setIsAdmin(true);
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('wiki_admin');
    setIsAdmin(false);
  }, []);

  return { isAdmin, login, logout };
}

export function useWikiProofread(slug) {
  const { adminState, refresh } = useAdminState();
  const [syncing, setSyncing] = useState(false);

  const isProofread = adminState?.approved?.[slug] === true;

  const toggleProofread = useCallback(() => {
    const next = !isProofread;
    const updated = { ...adminState };
    if (!updated.approved) updated.approved = {};
    if (next) {
      updated.approved[slug] = true;
    } else {
      delete updated.approved[slug];
    }
    refresh(updated);

    setSyncing(true);
    adminApiCall(next ? 'approve' : 'unapprove', slug)
      .catch(() => {})
      .finally(() => setSyncing(false));
  }, [slug, isProofread, adminState, refresh]);

  return { isProofread, toggleProofread, syncing };
}

export function useScrollSpy(ids) {
  const [activeId, setActiveId] = useState(() => (ids && ids.length ? ids[0] : null));

  useEffect(() => {
    if (!ids || ids.length === 0) return;

    setActiveId(ids[0]);

    const onScroll = () => {
      if (window.scrollY < 120) {
        setActiveId(ids[0]);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (window.scrollY < 120) return;
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [ids]);

  return activeId;
}

export function useReviewComments(slug) {
  const { adminState, refresh } = useAdminState();

  const comments = adminState?.comments?.[slug] || [];

  const addComment = useCallback(
    (selectedText, text) => {
      const entry = {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        selectedText,
        text,
        createdAt: new Date().toISOString(),
      };
      const updated = { ...adminState };
      if (!updated.comments) updated.comments = {};
      updated.comments[slug] = [...(updated.comments[slug] || []), entry];
      refresh(updated);

      adminApiCall('add_comment', slug, {
        comment: { selectedText, text },
      }).catch(() => {});
    },
    [slug, adminState, refresh],
  );

  const removeComment = useCallback(
    (id) => {
      const updated = { ...adminState };
      if (updated.comments?.[slug]) {
        updated.comments[slug] = updated.comments[slug].filter(
          (c) => c.id !== id,
        );
        if (updated.comments[slug].length === 0) delete updated.comments[slug];
      }
      refresh(updated);

      adminApiCall('remove_comment', slug, {
        comment: { id },
      }).catch(() => {});
    },
    [slug, adminState, refresh],
  );

  return { comments, addComment, removeComment };
}
